import React, { useState } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Grid, Chip, Paper, Button, CircularProgress, useTheme, useMediaQuery, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import { Transaction } from './TransactionsTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore } from '../utils/transactionStore';
interface TransactionDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction;
  onAction?: (actionType: string, reason?: string) => Promise<void>;
}
const TransactionDetailsDrawer: React.FC<TransactionDetailsDrawerProps> = ({
  open,
  onClose,
  transaction,
  onAction
}) => {
  const [expandedState, setExpandedState] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    showToast
  } = useToast();
  const manualSettlement = transactionStore.getManualSettlementForTransaction(transaction.id);
  const hasManualSettlementHistory = (transaction as any).manualSettlementHistory?.length > 0 || manualSettlement && manualSettlement.history.length > 0;
  const getSettlementHistory = () => {
    if (manualSettlement && manualSettlement.history.length > 0) {
      return manualSettlement.history;
    }
    return (transaction as any).manualSettlementHistory || [];
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  const formatDuration = (ms?: number) => {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };
  const getStatusColor = (status: string) => {
    const colors: Record<string, {
      bg: string;
      color: string;
    }> = {
      IN_PROGRESS: {
        bg: 'rgba(59, 130, 246, 0.1)',
        color: '#3B82F6'
      },
      SUCCESS: {
        bg: 'rgba(16, 185, 129, 0.1)',
        color: '#10B981'
      },
      FAILED: {
        bg: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444'
      },
      IN_REVIEW: {
        bg: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B'
      },
      PENDING_INQUIRY: {
        bg: 'rgba(139, 92, 246, 0.1)',
        color: '#8B5CF6'
      },
      PASS: {
        bg: 'rgba(16, 185, 129, 0.1)',
        color: '#10B981'
      },
      FAIL: {
        bg: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444'
      },
      PENDING: {
        bg: 'rgba(59, 130, 246, 0.1)',
        color: '#3B82F6'
      },
      REVIEW: {
        bg: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B'
      }
    };
    return colors[status] || {
      bg: 'rgba(100, 116, 139, 0.1)',
      color: '#64748B'
    };
  };
  const getStateIcon = (stateCode: string) => {
    const icons: Record<string, string> = {
      SCREENING: 'lucide:shield-check',
      TITLE_FETCH: 'lucide:id-card',
      RAAST_TITLE_FETCH: 'lucide:id-card',
      ONELINK_TITLE_FETCH: 'lucide:id-card',
      BALANCE_CHECK: 'lucide:building-2',
      RTGS_BALANCE_CHECK: 'lucide:building-2',
      FT_POST: 'lucide:send',
      RAAST_POST: 'lucide:send',
      ONELINK_POST: 'lucide:send',
      RTGS_POST: 'lucide:send',
      SUCCESS: 'lucide:check-circle',
      FAILED: 'lucide:x-circle'
    };
    return icons[stateCode] || 'lucide:circle';
  };
  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      PASS: 'lucide:check',
      FAIL: 'lucide:x',
      PENDING: 'lucide:clock',
      REVIEW: 'lucide:alert-triangle'
    };
    return icons[status] || 'lucide:help-circle';
  };
  const handleAction = async (actionType: string) => {
    if (!onAction) return;
    setActionLoading(true);
    try {
      await onAction(actionType);
      showToast(`${actionType} completed successfully`, 'success');
    } catch (error) {
      showToast(`${actionType} failed`, 'error');
    } finally {
      setActionLoading(false);
    }
  };
  const renderOverviewAccordion = () => <Accordion defaultExpanded data-id="element-2183">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2185" />} data-id="element-2184">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2186">
          <Icon icon="lucide:info" width={20} color="#0EA5E9" data-id="element-2187" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2188">
            Overview
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2189">
        <Grid container spacing={2} data-id="element-2190">
          <Grid item xs={12} sm={6} data-id="element-2191">
            <Typography variant="caption" color="text.secondary" data-id="element-2192">
              Transaction ID
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-2193">
              {transaction.id}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2194">
            <Typography variant="caption" color="text.secondary" data-id="element-2195">
              Bank Reference
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-2196">
              {transaction.bankRef}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2197">
            <Typography variant="caption" color="text.secondary" data-id="element-2198">
              Created At
            </Typography>
            <Typography variant="body2" data-id="element-2199">
              {formatDate(transaction.createdAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2200">
            <Typography variant="caption" color="text.secondary" data-id="element-2201">
              Last Updated
            </Typography>
            <Typography variant="body2" data-id="element-2202">
              {formatDate(transaction.updatedAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2203">
            <Typography variant="caption" color="text.secondary" data-id="element-2204">
              Current State
            </Typography>
            <Chip label={transaction.currentState.replace(/_/g, ' ')} size="small" sx={{
            backgroundColor: 'rgba(100, 116, 139, 0.1)',
            color: '#64748B',
            fontWeight: 600,
            fontSize: '0.7rem',
            mt: 0.5
          }} data-id="element-2205" />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>;
  const renderPartiesAccordion = () => <Accordion data-id="element-2206">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2208" />} data-id="element-2207">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2209">
          <Icon icon="lucide:users" width={20} color="#0EA5E9" data-id="element-2210" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2211">
            Parties
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2212">
        <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-2213">
          Remitter
        </Typography>
        <Paper variant="outlined" sx={{
        p: 2,
        mb: 3,
        borderRadius: 2
      }} data-id="element-2214">
          <Typography variant="body1" fontWeight={600} data-id="element-2215">
            {transaction.remitterName}
          </Typography>
        </Paper>
        <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-2216">
          Beneficiary
        </Typography>
        <Paper variant="outlined" sx={{
        p: 2,
        borderRadius: 2
      }} data-id="element-2217">
          <Typography variant="body1" fontWeight={600} data-id="element-2218">
            {transaction.beneficiaryName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
          mt: 1
        }} data-id="element-2219">
            IBAN: {transaction.beneficiaryIBAN}
          </Typography>
        </Paper>
      </AccordionDetails>
    </Accordion>;
  const renderAccountsAccordion = () => <Accordion data-id="element-2220">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2222" />} data-id="element-2221">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2223">
          <Icon icon="lucide:building-2" width={20} color="#6366F1" data-id="element-2224" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2225">
            Accounts
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2226">
        <Typography variant="subtitle2" gutterBottom data-id="element-2227">
          Beneficiary Account
        </Typography>
        <Paper variant="outlined" sx={{
        p: 2,
        borderRadius: 2
      }} data-id="element-2228">
          <Grid container spacing={2} data-id="element-2229">
            <Grid item xs={12} data-id="element-2230">
              <Typography variant="caption" color="text.secondary" data-id="element-2231">
                IBAN
              </Typography>
              <Typography variant="body2" fontFamily="monospace" data-id="element-2232">
                {transaction.beneficiaryIBAN}
              </Typography>
            </Grid>
            <Grid item xs={12} data-id="element-2233">
              <Typography variant="caption" color="text.secondary" data-id="element-2234">
                Account Title
              </Typography>
              <Typography variant="body2" data-id="element-2235">
                {transaction.beneficiaryName}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </AccordionDetails>
    </Accordion>;
  const renderAmountsAccordion = () => <Accordion data-id="element-2236">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2238" />} data-id="element-2237">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2239">
          <Icon icon="lucide:banknote" width={20} color="#10B981" data-id="element-2240" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2241">
            Amounts & FX
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2242">
        <Grid container spacing={2} data-id="element-2243">
          <Grid item xs={12} sm={6} data-id="element-2244">
            <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-2245">
              <Typography variant="caption" color="text.secondary" data-id="element-2246">
                Foreign Currency Amount
              </Typography>
              <Typography variant="h6" color="primary.main" data-id="element-2247">
                {transaction.currencyFCY} {transaction.amountFCY}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2248">
            <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-2249">
              <Typography variant="caption" color="text.secondary" data-id="element-2250">
                Local Amount
              </Typography>
              <Typography variant="h6" color="primary.main" data-id="element-2251">
                {transaction.amountPKR}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} data-id="element-2252">
            <Typography variant="caption" color="text.secondary" data-id="element-2253">
              Purpose
            </Typography>
            <Typography variant="body1" data-id="element-2254">
              {transaction.purposeText} ({transaction.purposeCode})
            </Typography>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>;
  const renderComplianceAccordion = () => <Accordion data-id="element-2255">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2257" />} data-id="element-2256">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2258">
          <Icon icon="lucide:shield-check" width={20} color="#F59E0B" data-id="element-2259" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2260">
            Screening Summary
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2261">
        <Grid container spacing={2} data-id="element-2262">
          <Grid item xs={12} data-id="element-2263">
            <Typography variant="subtitle2" gutterBottom data-id="element-2264">
              Screening Status
            </Typography>
            <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-2265">
              {transaction.stateHistory.find(s => s.stateCode === 'SCREENING') ? <>
                  <Chip icon={<Icon icon={getStatusIcon(transaction.stateHistory.find(s => s.stateCode === 'SCREENING')!.status)} width={14} data-id="element-2267" />} label={transaction.stateHistory.find(s => s.stateCode === 'SCREENING')!.status} size="small" sx={{
                ...getStatusColor(transaction.stateHistory.find(s => s.stateCode === 'SCREENING')!.status),
                fontWeight: 600
              }} data-id="element-2266" />
                  <Typography variant="body2" color="text.secondary" sx={{
                mt: 1
              }} data-id="element-2268">
                    Completed:{' '}
                    {formatDate(transaction.stateHistory.find(s => s.stateCode === 'SCREENING')!.timestamp)}
                  </Typography>
                </> : <Typography variant="body2" color="text.secondary" data-id="element-2269">
                  Not yet screened
                </Typography>}
            </Paper>
          </Grid>
          {transaction.ePRCNo && <Grid item xs={12} data-id="element-2270">
              <Typography variant="subtitle2" gutterBottom data-id="element-2271">
                e-PRC Details
              </Typography>
              <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-2272">
                <Typography variant="body2" data-id="element-2273">
                  <strong data-id="element-2274">e-PRC Number:</strong> {transaction.ePRCNo}
                </Typography>
                {transaction.ePRCDate && <Typography variant="body2" sx={{
              mt: 1
            }} data-id="element-2275">
                    <strong data-id="element-2276">Issue Date:</strong>{' '}
                    {formatDate(transaction.ePRCDate)}
                  </Typography>}
              </Paper>
            </Grid>}
        </Grid>
      </AccordionDetails>
    </Accordion>;
  const renderTimelineAccordion = () => {
    if (transaction.stateHistory.length === 0) {
      return <Accordion data-id="element-2277">
          <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2279" />} data-id="element-2278">
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-2280">
              <Icon icon="lucide:clock" width={20} color="#6366F1" data-id="element-2281" />
              <Typography variant="subtitle2" fontWeight={600} data-id="element-2282">
                Timeline
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-id="element-2283">
            <Box sx={{
            py: 4,
            textAlign: 'center'
          }} data-id="element-2284">
              <Icon icon="lucide:clock" width={48} height={48} color="#94A3B8" data-id="element-2285" />
              <Typography variant="body1" color="text.secondary" sx={{
              mt: 2
            }} data-id="element-2286">
                No state history available
              </Typography>
              <Typography variant="body2" color="text.secondary" data-id="element-2287">
                The transaction has not progressed through any states yet
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>;
    }
    return <Accordion data-id="element-2288">
        <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2290" />} data-id="element-2289">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-2291">
            <Icon icon="lucide:clock" width={20} color="#6366F1" data-id="element-2292" />
            <Typography variant="subtitle2" fontWeight={600} data-id="element-2293">
              Timeline
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails data-id="element-2294">
          <Box sx={{
          position: 'relative'
        }} data-id="element-2295">
            {/* Timeline line */}
            <Box sx={{
            position: 'absolute',
            left: 16,
            top: 24,
            bottom: 24,
            width: 2,
            background: 'linear-gradient(180deg, #22D3EE 0%, #6366F1 100%)',
            opacity: 0.3
          }} data-id="element-2296" />
            {transaction.stateHistory.map((state, index) => {
            const isExpanded = expandedState === `${state.stateCode}-${index}`;
            const statusColors = getStatusColor(state.status);
            return <Box key={`${state.stateCode}-${index}`} sx={{
              position: 'relative',
              mb: 3
            }} data-id="element-2297">
                  {/* Timeline node */}
                  <Box sx={{
                position: 'absolute',
                left: 8,
                top: 8,
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: statusColors.color,
                border: '3px solid white',
                boxShadow: `0 0 0 2px ${statusColors.color}`,
                zIndex: 1
              }} data-id="element-2298" />
                  <Accordion expanded={isExpanded} onChange={() => setExpandedState(isExpanded ? null : `${state.stateCode}-${index}`)} sx={{
                ml: 5
              }} data-id="element-2299">
                    <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={16} data-id="element-2301" />} sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(79, 70, 229, 0.04)'
                  }
                }} data-id="element-2300">
                      <Box sx={{
                    width: '100%'
                  }} data-id="element-2302">
                        <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1
                    }} data-id="element-2303">
                          <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }} data-id="element-2304">
                            <Icon icon={getStateIcon(state.stateCode)} width={20} height={20} color={statusColors.color} data-id="element-2305" />
                            <Typography variant="subtitle2" fontWeight={600} data-id="element-2306">
                              {state.stateCode.replace(/_/g, ' ')}
                            </Typography>
                          </Box>
                          <Chip icon={<Icon icon={getStatusIcon(state.status)} width={12} data-id="element-2308" />} label={state.status} size="small" sx={{
                        backgroundColor: statusColors.bg,
                        color: statusColors.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 22
                      }} data-id="element-2307" />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" data-id="element-2309">
                          {formatDate(state.timestamp)}
                        </Typography>
                        {state.failReasonCode && <Typography variant="body2" color="error.main" sx={{
                      mt: 1
                    }} data-id="element-2310">
                            {state.failReasonCode.replace(/_/g, ' ')}
                          </Typography>}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails data-id="element-2311">
                      <Grid container spacing={2} data-id="element-2312">
                        {state.primaryRef && <Grid item xs={12} sm={6} data-id="element-2313">
                            <Typography variant="caption" color="text.secondary" data-id="element-2314">
                              Primary Reference
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace" data-id="element-2315">
                              {state.primaryRef}
                            </Typography>
                          </Grid>}
                        {state.durationMs !== undefined && <Grid item xs={12} sm={6} data-id="element-2316">
                            <Typography variant="caption" color="text.secondary" data-id="element-2317">
                              Duration
                            </Typography>
                            <Typography variant="body2" data-id="element-2318">
                              {formatDuration(state.durationMs)}
                            </Typography>
                          </Grid>}
                        {state.attempts && <Grid item xs={12} sm={6} data-id="element-2319">
                            <Typography variant="caption" color="text.secondary" data-id="element-2320">
                              Attempts
                            </Typography>
                            <Typography variant="body2" data-id="element-2321">
                              {state.attempts}
                            </Typography>
                          </Grid>}
                        {state.requestSnippet && <Grid item xs={12} data-id="element-2322">
                            <Typography variant="caption" color="text.secondary" data-id="element-2323">
                              Request
                            </Typography>
                            <Paper variant="outlined" sx={{
                        p: 1,
                        mt: 0.5,
                        backgroundColor: '#F8FAFC',
                        overflow: 'auto'
                      }} data-id="element-2324">
                              <Typography variant="body2" component="pre" sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all'
                        }} data-id="element-2325">
                                {state.requestSnippet}
                              </Typography>
                            </Paper>
                          </Grid>}
                        {state.responseSnippet && <Grid item xs={12} data-id="element-2326">
                            <Typography variant="caption" color="text.secondary" data-id="element-2327">
                              Response
                            </Typography>
                            <Paper variant="outlined" sx={{
                        p: 1,
                        mt: 0.5,
                        backgroundColor: '#F8FAFC',
                        overflow: 'auto'
                      }} data-id="element-2328">
                              <Typography variant="body2" component="pre" sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all'
                        }} data-id="element-2329">
                                {state.responseSnippet}
                              </Typography>
                            </Paper>
                          </Grid>}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Box>;
          })}
          </Box>
        </AccordionDetails>
      </Accordion>;
  };
  const renderAmendmentHistoryAccordion = () => {
    const hasAmendments = transaction.amendmentHistory && transaction.amendmentHistory.length > 0;
    const hasManualComment = transaction.manualActionComment;
    if (!hasAmendments && !hasManualComment) return null;
    return <Accordion data-id="element-2330">
        <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2332" />} data-id="element-2331">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-2333">
            <Icon icon="lucide:history" width={20} color="#F59E0B" data-id="element-2334" />
            <Typography variant="subtitle2" fontWeight={600} data-id="element-2335">
              Amendment & Action History
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails data-id="element-2336">
          {hasManualComment && <Box sx={{
          mb: hasAmendments ? 4 : 0
        }} data-id="element-2337">
              <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-2338">
                Manual Action Comment
              </Typography>
              <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2,
            borderLeft: `4px solid ${transaction.manualActionComment!.actionType === 'MANUAL_FAIL' ? '#EF4444' : '#10B981'}`
          }} data-id="element-2339">
                <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1
            }} data-id="element-2340">
                  <Chip label={transaction.manualActionComment!.actionType.replace('_', ' ')} size="small" sx={{
                bgcolor: transaction.manualActionComment!.actionType === 'MANUAL_FAIL' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                color: transaction.manualActionComment!.actionType === 'MANUAL_FAIL' ? '#EF4444' : '#10B981',
                fontWeight: 600,
                fontSize: '0.7rem'
              }} data-id="element-2341" />
                  <Typography variant="caption" color="text.secondary" data-id="element-2342">
                    {formatDate(transaction.manualActionComment!.timestamp)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{
              mt: 1,
              fontStyle: 'italic'
            }} data-id="element-2343">
                  "{transaction.manualActionComment!.comment}"
                </Typography>
              </Paper>
            </Box>}

          {hasAmendments && <Box data-id="element-2344">
              <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-2345">
                Amendment History
              </Typography>
              <Box sx={{
            position: 'relative'
          }} data-id="element-2346">
                <Box sx={{
              position: 'absolute',
              left: 16,
              top: 24,
              bottom: 24,
              width: 2,
              background: '#E2E8F0'
            }} data-id="element-2347" />

                {transaction.amendmentHistory!.map((amend, index) => <Box key={index} sx={{
              position: 'relative',
              mb: 3
            }} data-id="element-2348">
                    <Box sx={{
                position: 'absolute',
                left: 8,
                top: 8,
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: amend.status === 'APPROVED' ? '#10B981' : amend.status === 'REJECTED' ? '#EF4444' : '#F59E0B',
                border: '3px solid white',
                boxShadow: '0 0 0 1px #E2E8F0',
                zIndex: 1
              }} data-id="element-2349" />

                    <Paper variant="outlined" sx={{
                ml: 5,
                p: 2,
                borderRadius: 2
              }} data-id="element-2350">
                      <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2
                }} data-id="element-2351">
                        <Box data-id="element-2352">
                          <Chip label={amend.status} size="small" sx={{
                      bgcolor: amend.status === 'APPROVED' ? 'rgba(16, 185, 129, 0.1)' : amend.status === 'REJECTED' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                      color: amend.status === 'APPROVED' ? '#10B981' : amend.status === 'REJECTED' ? '#EF4444' : '#F59E0B',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 1
                    }} data-id="element-2353" />
                          <Typography variant="caption" color="text.secondary" display="block" data-id="element-2354">
                            Requested by {amend.requestedBy} on{' '}
                            {formatDate(amend.timestamp)}
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={2} sx={{
                  mb: amend.checkerComment ? 2 : 0
                }} data-id="element-2355">
                        <Grid item xs={12} sm={6} data-id="element-2356">
                          <Typography variant="caption" color="text.secondary" data-id="element-2357">
                            Original Account
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace" sx={{
                      textDecoration: 'line-through',
                      color: 'text.secondary'
                    }} data-id="element-2358">
                            {amend.oldAccount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-id="element-2359">
                          <Typography variant="caption" color="text.secondary" data-id="element-2360">
                            New Account
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="primary.main" data-id="element-2361">
                            {amend.newAccount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} data-id="element-2362">
                          <Typography variant="caption" color="text.secondary" data-id="element-2363">
                            Fetched Title
                          </Typography>
                          <Typography variant="body2" fontWeight={500} data-id="element-2364">
                            {amend.fetchedTitle}
                          </Typography>
                        </Grid>
                      </Grid>

                      {amend.checkerComment && <Box sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: '#F8FAFC',
                  borderRadius: 1
                }} data-id="element-2365">
                          <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-2366">
                            Checker Comment
                          </Typography>
                          <Typography variant="body2" sx={{
                    mt: 0.5
                  }} data-id="element-2367">
                            {amend.checkerComment}
                          </Typography>
                        </Box>}
                    </Paper>
                  </Box>)}
              </Box>
            </Box>}
        </AccordionDetails>
      </Accordion>;
  };
  const renderActionButtons = () => {
    const buttons = [];
    if (transaction.lifecycleStatus === 'IN_REVIEW' && transaction.currentState === 'SCREENING') {
      buttons.push(<Button key="approve" variant="contained" color="success" startIcon={<Icon icon="lucide:check" width={16} data-id="element-2369" />} onClick={() => handleAction('approve')} disabled={actionLoading} data-id="element-2368">
          Approve
        </Button>, <Button key="reject" variant="outlined" color="error" startIcon={<Icon icon="lucide:x" width={16} data-id="element-2371" />} onClick={() => handleAction('reject')} disabled={actionLoading} data-id="element-2370">
          Reject
        </Button>);
    }
    if (transaction.lifecycleStatus === 'PENDING_INQUIRY') {
      buttons.push(<Button key="inquiry" variant="contained" startIcon={actionLoading ? <CircularProgress size={16} data-id="element-2373" /> : <Icon icon="lucide:search" width={16} data-id="element-2374" />} onClick={() => handleAction('inquiry')} disabled={actionLoading} data-id="element-2372">
          Run Inquiry
        </Button>);
    }
    if (transaction.lifecycleStatus === 'SUCCESS') {
      buttons.push(<Button key="download-eprc" variant="outlined" startIcon={<Icon icon="lucide:download" width={16} data-id="element-2376" />} onClick={() => handleAction('download-eprc')} disabled={actionLoading} data-id="element-2375">
          Download e-PRC
        </Button>, <Button key="reverse" variant="outlined" color="error" startIcon={<Icon icon="lucide:undo-2" width={16} data-id="element-2378" />} onClick={() => handleAction('reverse')} disabled={actionLoading} data-id="element-2377">
          Reverse
        </Button>);
    }
    return buttons;
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: isMobile ? '100%' : 560,
      maxWidth: '100%'
    }
  }} data-id="element-2379">
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }} data-id="element-2380">
        {/* Header */}
        <Box sx={{
        p: 3,
        pb: 2
      }} data-id="element-2381">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2
        }} data-id="element-2382">
            <Typography variant="h6" data-id="element-2383">Transaction Details</Typography>
            <IconButton onClick={onClose} size="small" data-id="element-2384">
              <Icon icon="lucide:x" width={20} data-id="element-2385" />
            </IconButton>
          </Box>
          {/* Header Chips */}
          <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2
        }} data-id="element-2386">
            <Chip label={transaction.payoutType} size="small" sx={{
            backgroundColor: transaction.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(139, 92, 246, 0.1)',
            color: transaction.payoutType === 'FT' ? '#0EA5E9' : '#8B5CF6',
            fontWeight: 600
          }} data-id="element-2387" />
            <Chip label={transaction.lifecycleStatus.replace(/_/g, ' ')} size="small" sx={{
            ...getStatusColor(transaction.lifecycleStatus),
            fontWeight: 600
          }} data-id="element-2388" />
            <Chip label={`${transaction.currencyFCY} ${transaction.amountFCY}`} size="small" sx={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            color: '#10B981',
            fontWeight: 600
          }} data-id="element-2389" />
            <Chip label={transaction.corridor} size="small" sx={{
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            color: '#6366F1',
            fontWeight: 600
          }} data-id="element-2390" />
          </Box>
          {/* Subheader */}
          <Box data-id="element-2391">
            <Typography variant="subtitle2" color="text.secondary" gutterBottom data-id="element-2392">
              Remitting Institute (MTO)
            </Typography>
            {transaction.mtoName ? <Typography variant="h6" color="primary.main" gutterBottom data-id="element-2393">
                {transaction.mtoName}
              </Typography> : <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1
          }} data-id="element-2394">
                <Typography variant="h6" color="text.secondary" data-id="element-2395">
                  —
                </Typography>
                <Chip label="No MTO provided" size="small" sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              fontWeight: 600
            }} data-id="element-2396" />
              </Box>}
            <Typography variant="body2" color="text.secondary" data-id="element-2397">
              MTO Ref: {transaction.mtoRef} • Bank Ref: {transaction.bankRef}
            </Typography>
            {(transaction.railRefs.RRN || transaction.railRefs.RaastMsgId) && <Typography variant="body2" color="text.secondary" data-id="element-2398">
                Rail Refs:{' '}
                {[transaction.railRefs.RaastMsgId && `Raast: ${transaction.railRefs.RaastMsgId}`, transaction.railRefs.RRN && `RRN: ${transaction.railRefs.RRN}`, transaction.railRefs.STAN && `STAN: ${transaction.railRefs.STAN}`, transaction.railRefs.UETR && `UETR: ${transaction.railRefs.UETR}`].filter(Boolean).join(' • ')}
              </Typography>}
          </Box>
        </Box>
        <Divider data-id="element-2399" />
        {/* Accordion Content */}
        <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 3
      }} data-id="element-2400">
          {renderOverviewAccordion()}
          {renderPartiesAccordion()}
          {renderAccountsAccordion()}
          {renderAmountsAccordion()}
          {renderComplianceAccordion()}
          {renderTimelineAccordion()}
          {renderAmendmentHistoryAccordion()}
        </Box>
        {/* Action Buttons */}
        {renderActionButtons().length > 0 && <>
            <Divider data-id="element-2401" />
            <Box sx={{
          p: 3,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap'
        }} data-id="element-2402">
              {renderActionButtons()}
            </Box>
          </>}
      </Box>
    </Drawer>;
};
export default TransactionDetailsDrawer;